import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import type { DiagramData, SelectedCardType } from '../Types/diagram-type';
import { httpService } from '../../../utility/httpService';
import { API_BASE } from '../../../config/env';
import { httpConstants } from '../../../constants';

interface UseReportGenerationProps {
  diagramData: DiagramData | null;
  selectedCard?: SelectedCardType | Partial<SelectedCardType> | null;
}

interface ReportResponse {
  detail: string;
  file_name: string;
  content_type: string;
  file_data_base64: string;
}

// NOT async — React hooks must be synchronous
export const useReportGeneration = ({ diagramData, selectedCard }: UseReportGenerationProps) => {
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [downloadProgressOpen, setDownloadProgressOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const useCaseId = useSelector((state: RootState) => state.newIdeation.id);

  // Defined inside the hook but NOT called at the top level
  const fetchReportFromBackend = async (
    diagramId: string,
    caseId: string
  ): Promise<ReportResponse> => {
    try {
      const response = await httpService<ReportResponse>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/e2e_taxonomy/opportunities/${diagramId}/report/?use_case_id=${caseId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching report from backend:', error);
      throw error;
    }
  };

  // Synchronous — converts base64 to Blob and triggers download
  const downloadFileFromBase64 = (base64Data: string, fileName: string, contentType: string) => {
    const binaryStr = atob(base64Data);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: contentType });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadReport = async () => {
    setLoadingDocument(true);
    setDownloadProgressOpen(true);
    setDownloadProgress(0);

    try {
      // Resolve use case ID
      const selectedCardUseCaseId =
        selectedCard?.useCaseId || selectedCard?.use_case_id || selectedCard?.id;
      const caseIdToUse: string | undefined =
        typeof selectedCardUseCaseId === 'string' ? selectedCardUseCaseId : useCaseId;

      if (!caseIdToUse || typeof caseIdToUse !== 'string') {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      // Resolve diagram ID
      const diagramId =
        diagramData?.diagram?.id || (diagramData as any)?.DiagramData?.diagram?.id || '';

      if (!diagramId) {
        ShowToast({ message: 'Diagram ID not found', type: ToastTypeEnum.Error });
        return;
      }

      setDownloadProgress(30);

      // Fetch report (base64) from backend
      let reportResponse = await fetchReportFromBackend(diagramId, caseIdToUse);

      setDownloadProgress(60);

      // Trigger download directly from base64
      downloadFileFromBase64(
        reportResponse.file_data_base64,
        reportResponse.file_name,
        reportResponse.content_type
      );

      setDownloadProgress(100);

      ShowToast({ message: 'Report downloaded successfully', type: ToastTypeEnum.Success });

      setTimeout(() => {
        setDownloadProgressOpen(false);
        setDownloadProgress(0);
      }, 1500);
    } catch (err) {
      console.error('Word document download failed', err);
      setDownloadProgressOpen(false);
      setDownloadProgress(0);
      ShowToast({ message: 'Failed to download report', type: ToastTypeEnum.Error });
    } finally {
      setLoadingDocument(false);
    }
  };

  return {
    loadingDocument,
    downloadProgressOpen,
    downloadProgress,
    handleDownloadReport,
  };
};
